import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface DownloadProgress {
  current: number;
  total: number;
}

export type DownloadStatus = 'running' | 'ready' | 'error';

export interface DownloadItem {
  id: string;
  label: string;
  hint?: string;
  progress: DownloadProgress | null;
  status: DownloadStatus;
  cancel?: () => void;
  /** Trigger file save — available when status is ready. */
  download?: () => void;
  /** Remove from the downloads tray. */
  dismiss?: () => void;
}

interface StoredDownloadBlob {
  blob: Blob;
  filename: string;
  objectUrl: string;
}

/** Serializable tray entry — only ready downloads are persisted across refresh. */
interface SerializedReadyDownload {
  id: string;
  label: string;
  hint?: string;
  status: 'ready';
  filename: string;
}

interface SerializedBlobPayload {
  filename: string;
  mimeType: string;
  contentBase64: string;
}

const SESSION_ITEMS_KEY = 'ws-downloads:ready-items';
const SESSION_BLOB_KEY_PREFIX = 'ws-downloads:blob:';

@Injectable({ providedIn: 'root' })
export class WsDownloadsService {
  readonly downloads$ = new BehaviorSubject<DownloadItem[]>([]);

  private readonly blobStore = new Map<string, StoredDownloadBlob>();

  constructor() {
    this.restoreReadyDownloadsFromSession();
  }

  upsertDownload(item: DownloadItem): void {
    const downloads = [...this.downloads$.value];
    const index = downloads.findIndex((entry) => entry.id === item.id);
    if (index >= 0) {
      downloads[index] = item;
    } else {
      downloads.unshift(item);
    }
    this.downloads$.next(downloads);
  }

  /**
   * Marks a job complete and keeps the CSV in memory (+ sessionStorage) for re-download after refresh.
   */
  completeDownload(id: string, item: Pick<DownloadItem, 'id' | 'label' | 'hint'> & { blob: Blob; filename: string }): void {
    this.releaseBlob(id);

    const objectUrl = URL.createObjectURL(item.blob);
    this.blobStore.set(id, { blob: item.blob, filename: item.filename, objectUrl });

    this.upsertDownload({
      id: item.id,
      label: item.label,
      hint: item.hint,
      progress: null,
      status: 'ready',
      download: () => this.triggerDownload(id),
      dismiss: () => this.removeDownload(id),
    });

    void this.persistReadyDownload(id, item.label, item.hint, item.filename, item.blob);
  }

  triggerDownload(id: string): void {
    const stored = this.blobStore.get(id);
    if (!stored) {
      return;
    }

    const anchor = document.createElement('a');
    anchor.href = stored.objectUrl;
    anchor.download = stored.filename;
    anchor.click();
  }

  removeDownload(id: string): void {
    this.releaseBlob(id);
    this.removeReadyDownloadFromSession(id);
    const downloads = this.downloads$.value.filter((entry) => entry.id !== id);
    if (downloads.length !== this.downloads$.value.length) {
      this.downloads$.next(downloads);
    }
  }

  hasDownload(id: string): boolean {
    return this.downloads$.value.some((entry) => entry.id === id);
  }

  progressPercent(item: DownloadItem): number {
    if (item.status === 'ready') {
      return 100;
    }
    const progress = item.progress;
    if (!progress?.total) {
      return 0;
    }
    return Math.min(100, Math.round((progress.current / progress.total) * 100));
  }

  private restoreReadyDownloadsFromSession(): void {
    if (!this.isSessionStorageAvailable()) {
      return;
    }

    try {
      const raw = sessionStorage.getItem(SESSION_ITEMS_KEY);
      if (!raw) {
        return;
      }

      const items = JSON.parse(raw) as SerializedReadyDownload[];
      if (!Array.isArray(items) || !items.length) {
        return;
      }

      const restored: DownloadItem[] = [];
      for (const item of items) {
        const blobPayload = this.readBlobPayloadFromSession(item.id);
        if (!blobPayload) {
          continue;
        }

        const blob = this.base64ToBlob(blobPayload.contentBase64, blobPayload.mimeType);
        const objectUrl = URL.createObjectURL(blob);
        this.blobStore.set(item.id, {
          blob,
          filename: blobPayload.filename,
          objectUrl,
        });

        restored.push({
          id: item.id,
          label: item.label,
          hint: item.hint ?? blobPayload.filename,
          progress: null,
          status: 'ready',
          download: () => this.triggerDownload(item.id),
          dismiss: () => this.removeDownload(item.id),
        });
      }

      if (restored.length) {
        this.downloads$.next(restored);
      }
    } catch (err) {
      console.warn('Failed to restore downloads from sessionStorage', err);
    }
  }

  private async persistReadyDownload(
    id: string,
    label: string,
    hint: string | undefined,
    filename: string,
    blob: Blob
  ): Promise<void> {
    if (!this.isSessionStorageAvailable()) {
      return;
    }

    try {
      const contentBase64 = await this.blobToBase64(blob);
      const blobPayload: SerializedBlobPayload = {
        filename,
        mimeType: blob.type || 'text/csv;charset=utf-8',
        contentBase64,
      };
      sessionStorage.setItem(SESSION_BLOB_KEY_PREFIX + id, JSON.stringify(blobPayload));

      const items = this.readReadyItemsFromSession().filter((entry) => entry.id !== id);
      items.unshift({ id, label, hint, status: 'ready', filename });
      sessionStorage.setItem(SESSION_ITEMS_KEY, JSON.stringify(items));
    } catch (err) {
      console.warn('Failed to persist download to sessionStorage (quota or encoding)', err);
    }
  }

  private removeReadyDownloadFromSession(id: string): void {
    if (!this.isSessionStorageAvailable()) {
      return;
    }

    try {
      sessionStorage.removeItem(SESSION_BLOB_KEY_PREFIX + id);
      const items = this.readReadyItemsFromSession().filter((entry) => entry.id !== id);
      if (items.length) {
        sessionStorage.setItem(SESSION_ITEMS_KEY, JSON.stringify(items));
      } else {
        sessionStorage.removeItem(SESSION_ITEMS_KEY);
      }
    } catch (err) {
      console.warn('Failed to remove download from sessionStorage', err);
    }
  }

  private readReadyItemsFromSession(): SerializedReadyDownload[] {
    const raw = sessionStorage.getItem(SESSION_ITEMS_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  }

  private readBlobPayloadFromSession(id: string): SerializedBlobPayload | null {
    try {
      const raw = sessionStorage.getItem(SESSION_BLOB_KEY_PREFIX + id);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as SerializedBlobPayload;
    } catch {
      return null;
    }
  }

  private releaseBlob(id: string): void {
    const stored = this.blobStore.get(id);
    if (stored) {
      URL.revokeObjectURL(stored.objectUrl);
      this.blobStore.delete(id);
    }
  }

  private isSessionStorageAvailable(): boolean {
    try {
      const test = '__ws_downloads_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return blob.arrayBuffer().then((buffer) => this.arrayBufferToBase64(buffer));
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  }

  private base64ToBlob(base64: string, mimeType: string): Blob {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  }
}
